<template>
    <div v-if="show" class="modal-overlay" @click.self="closeModal">
        <div class="modal-container">

            <div class="modal-header">
                <h4 class="modal-title">
                    <i class="fa-solid" :class="editMode ? 'fa-user-pen' : 'fa-user-plus'"></i>
                    {{ editMode ? 'Edit User' : 'Create User' }}
                </h4>
                <button class="modal-close" @click="closeModal">
                    <i class="fa-solid fa-times"></i>
                </button>
            </div>

            <div class="modal-body">
                <div class="row g-3">

                    <!-- First Name -->
                    <div class="col-md-6">
                        <label class="form-label-theme">First Name <span class="required">*</span></label>
                        <input v-model="form.firstName" type="text" class="form-control-theme"
                            :class="{ 'is-invalid': errors.firstName }" placeholder="e.g. John" />
                        <div v-if="errors.firstName" class="validation-error">{{ errors.firstName }}</div>
                    </div>

                    <!-- Last Name -->
                    <div class="col-md-6">
                        <label class="form-label-theme">Last Name <span class="required">*</span></label>
                        <input v-model="form.lastName" type="text" class="form-control-theme"
                            :class="{ 'is-invalid': errors.lastName }" placeholder="e.g. Doe" />
                        <div v-if="errors.lastName" class="validation-error">{{ errors.lastName }}</div>
                    </div>

                    <!-- Email -->
                    <div class="col-md-6">
                        <label class="form-label-theme">Email <span class="required">*</span></label>
                        <input v-model="form.email" type="email" class="form-control-theme"
                            :class="{ 'is-invalid': errors.email }" placeholder="e.g. john@example.com" />
                        <div v-if="errors.email" class="validation-error">{{ errors.email }}</div>
                    </div>

                    <!-- Phone -->
                    <div class="col-md-6">
                        <label class="form-label-theme">Phone</label>
                        <input v-model="form.phoneNumber" type="number" class="form-control-theme"
                            placeholder="e.g. +1 555 000 0000" />
                    </div>

                    <!-- Role -->
                    <div class="col-md-6">
                        <label class="form-label-theme">Role <span class="required">*</span></label>
                        <select v-model="form.role" class="form-control-theme form-select-theme"
                            :class="{ 'is-invalid': errors.role }">
                            <option value="">Select role...</option>
                            <option value="super-admin">Super Admin</option>
                            <option value="hospital">Hospital</option>
                            <option value="doctor">Doctor</option>
                            <option value="patient">Patient</option>
                            <!-- <option value="staff">Staff</option> -->
                        </select>
                        <div v-if="errors.role" class="validation-error">{{ errors.role }}</div>
                    </div>

                    <!-- Status -->
                    <div class="col-md-6">
                        <label class="form-label-theme">Status</label>
                        <select v-model="form.active" class="form-control-theme form-select-theme">
                            <option :value="true">Active</option>
                            <option :value="false">Inactive</option>
                        </select>
                    </div>

                    <!-- Password — only shown when creating -->
                    <div class="col-md-6">
                        <label class="form-label-theme">Hospital</label>
                        <select v-model="form.hospitalId" class="form-control-theme form-select-theme"
                            :disabled="loadingHospitals">
                            <option value="">Select hospital...</option>
                            <option v-for="hospital in hospitals" :key="hospital.id || hospital._id"
                                :value="hospital.id || hospital._id">
                                {{ hospital.name }}
                            </option>
                        </select>
                    </div>

                    <div class="col-md-6">
                        <label class="form-label-theme">Date of Birth</label>
                        <input v-model="form.dob" type="date" class="form-control-theme" />
                    </div>

                    <div v-if="!editMode" class="col-md-6">
                        <label class="form-label-theme">Password <span class="required">*</span></label>
                        <div class="password-wrapper">
                            <input v-model="form.password" :type="showPassword ? 'text' : 'password'"
                                class="form-control-theme" :class="{ 'is-invalid': errors.password }"
                                placeholder="Min. 8 characters" />
                            <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                                <i class="fa-solid" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                            </button>
                        </div>
                        <div v-if="errors.password" class="validation-error">{{ errors.password }}</div>
                    </div>

                    <!-- Confirm Password — only shown when creating -->
                    <div v-if="!editMode" class="col-md-6">
                        <label class="form-label-theme">Confirm Password <span class="required">*</span></label>
                        <div class="password-wrapper">
                            <input v-model="form.confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
                                class="form-control-theme" :class="{ 'is-invalid': errors.confirmPassword }"
                                placeholder="Re-enter password" />
                            <button type="button" class="password-toggle"
                                @click="showConfirmPassword = !showConfirmPassword">
                                <i class="fa-solid" :class="showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                            </button>
                        </div>
                        <div v-if="errors.confirmPassword" class="validation-error">{{ errors.confirmPassword }}</div>
                    </div>

                    <div v-if="editMode" class="col-12">
                        <div class="reset-password-panel">
                            <div class="reset-password-header">
                                <div>
                                    <h5>Reset Password</h5>
                                    <p>Set a new password for this user.</p>
                                </div>
                                <button class="btn-theme-secondary" type="button" @click="toggleResetPassword">
                                    {{ showResetPassword ? 'Cancel Reset' : 'Reset Password' }}
                                </button>
                            </div>

                            <div v-if="showResetPassword" class="row g-3 mt-1">
                                <div class="col-md-6">
                                    <label class="form-label-theme">New Password <span class="required">*</span></label>
                                    <div class="password-wrapper">
                                        <input v-model="resetPasswordForm.password"
                                            :type="showResetPasswordValue ? 'text' : 'password'"
                                            class="form-control-theme"
                                            :class="{ 'is-invalid': resetPasswordErrors.password }"
                                            placeholder="Min. 8 characters" />
                                        <button type="button" class="password-toggle"
                                            @click="showResetPasswordValue = !showResetPasswordValue">
                                            <i class="fa-solid"
                                                :class="showResetPasswordValue ? 'fa-eye-slash' : 'fa-eye'"></i>
                                        </button>
                                    </div>
                                    <div v-if="resetPasswordErrors.password" class="validation-error">
                                        {{ resetPasswordErrors.password }}
                                    </div>
                                </div>

                                <div class="col-md-6">
                                    <label class="form-label-theme">Confirm New Password <span
                                            class="required">*</span></label>
                                    <div class="password-wrapper">
                                        <input v-model="resetPasswordForm.confirmPassword"
                                            :type="showResetConfirmPasswordValue ? 'text' : 'password'"
                                            class="form-control-theme"
                                            :class="{ 'is-invalid': resetPasswordErrors.confirmPassword }"
                                            placeholder="Re-enter password" />
                                        <button type="button" class="password-toggle"
                                            @click="showResetConfirmPasswordValue = !showResetConfirmPasswordValue">
                                            <i class="fa-solid"
                                                :class="showResetConfirmPasswordValue ? 'fa-eye-slash' : 'fa-eye'"></i>
                                        </button>
                                    </div>
                                    <div v-if="resetPasswordErrors.confirmPassword" class="validation-error">
                                        {{ resetPasswordErrors.confirmPassword }}
                                    </div>
                                </div>

                                <div class="col-12">
                                    <button class="btn-theme-warning" type="button" :disabled="resettingPassword"
                                        @click="handleResetPassword">
                                        <span v-if="resettingPassword"
                                            class="spinner-border spinner-border-sm me-2"></span>
                                        Save New Password
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div class="modal-footer">
                <button class="btn-theme-secondary" @click="closeModal">Cancel</button>

                <button v-if="editMode" class="btn-theme-outline-danger" @click="handleDelete">
                    <i class="fa-solid fa-trash me-2"></i> Delete
                </button>

                <button class="btn-theme-success" :disabled="saving" @click="handleSave">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                    {{ editMode ? 'Update' : 'Create' }}
                </button>
            </div>

        </div>
    </div>
</template>

<script>
import { UsersService } from "@/services/users.service";
import { AuthService } from "@/services/auth.service";
import { lowercaseString } from "@/utils/helpers";

export default {
    name: "UserManagementModal",

    props: {
        show: Boolean,
        user: Object
    },

    emits: ["close", "saved", "deleted", "fetch-users"],

    data() {
        return {
            saving: false,
            resettingPassword: false,
            loadingHospitals: false,
            showPassword: false,
            showConfirmPassword: false,
            showResetPassword: false,
            showResetPasswordValue: false,
            showResetConfirmPasswordValue: false,
            form: {
                firstName: "",
                lastName: "",
                email: "",
                phoneNumber: "",
                role: "",
                active: true,
                hospitalId: "",
                dob: "",
                password: "",
                confirmPassword: ""
            },
            hospitals: [],
            resetPasswordForm: {
                password: "",
                confirmPassword: ""
            },
            errors: {
                firstName: "",
                lastName: "",
                email: "",
                role: "",
                password: "",
                confirmPassword: ""
            },
            resetPasswordErrors: {
                password: "",
                confirmPassword: ""
            }
        };
    },

    computed: {
        editMode() {
            return !!(this.user?._id || this.user?.id);
        }
    },

    watch: {
        show(val) {
            if (val) {
                this.loadForm();
                this.fetchHospitals();
            }
        },
        user() {
            this.loadForm();
        }
    },

    methods: {
        loadForm() {
            this.showPassword = false;
            this.showConfirmPassword = false;
            this.resetResetPasswordForm();
            if (this.editMode) {
                this.form = {
                    firstName: this.user.firstname || this.user.firstName || "",
                    lastName: this.user.lastname || this.user.lastName || "",
                    email: this.user.email || "",
                    phoneNumber: this.user.phoneNumber || this.user.phone || "",
                    role: this.user.objectType || this.user.role || "",
                    active: this.user.active !== undefined ? this.user.active : true,
                    hospitalId: this.user.hospitalId || this.user.hospital?.id || this.user.hospital?._id || "",
                    dob: this.formatDateInput(this.user.dob || this.user.dateOfBirth),
                    password: "",
                    confirmPassword: ""
                };
            } else {
                this.form = {
                    firstName: "",
                    lastName: "",
                    email: "",
                    phoneNumber: "",
                    role: "",
                    active: true,
                    hospitalId: "",
                    dob: "",
                    password: "",
                    confirmPassword: ""
                };
            }
            this.clearErrors();
        },

        clearErrors() {
            this.errors = {
                firstName: "",
                lastName: "",
                email: "",
                role: "",
                password: "",
                confirmPassword: ""
            };
        },

        async fetchHospitals() {
            if (this.hospitals.length || this.loadingHospitals) return;

            this.loadingHospitals = true;
            try {
                const response = await AuthService.getSuperAdminHospitals();
                this.hospitals = response.data?.items || response.data || [];
            } catch (error) {
                console.error("Error fetching hospitals:", error);
                this.$store.commit("toast/NEW", {
                    type: "error",
                    message: "Failed to load hospitals list"
                });
            } finally {
                this.loadingHospitals = false;
            }
        },

        formatDateInput(value) {
            if (!value) return "";
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return "";

            return date.toISOString().slice(0, 10);
        },

        resetResetPasswordForm() {
            this.showResetPassword = false;
            this.showResetPasswordValue = false;
            this.showResetConfirmPasswordValue = false;
            this.resetPasswordForm = {
                password: "",
                confirmPassword: ""
            };
            this.resetPasswordErrors = {
                password: "",
                confirmPassword: ""
            };
        },

        toggleResetPassword() {
            if (this.showResetPassword) {
                this.resetResetPasswordForm();
                return;
            }

            this.showResetPassword = true;
        },

        validate() {
            this.clearErrors();
            let isValid = true;

            if (!this.form.firstName.trim()) {
                this.errors.firstName = "First name is required";
                isValid = false;
            }

            if (!this.form.lastName.trim()) {
                this.errors.lastName = "Last name is required";
                isValid = false;
            }

            if (!this.form.email.trim()) {
                this.errors.email = "Email is required";
                isValid = false;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
                this.errors.email = "Please enter a valid email address";
                isValid = false;
            }

            if (!this.form.role) {
                this.errors.role = "Role is required";
                isValid = false;
            }

            if (!this.editMode) {
                if (!this.form.password) {
                    this.errors.password = "Password is required";
                    isValid = false;
                } else if (this.form.password.length < 8) {
                    this.errors.password = "Password must be at least 8 characters";
                    isValid = false;
                }

                if (!this.form.confirmPassword) {
                    this.errors.confirmPassword = "Please confirm your password";
                    isValid = false;
                } else if (this.form.password !== this.form.confirmPassword) {
                    this.errors.confirmPassword = "Passwords do not match";
                    isValid = false;
                }
            }

            return isValid;
        },

        validateResetPassword() {
            this.resetPasswordErrors = {
                password: "",
                confirmPassword: ""
            };

            let isValid = true;

            if (!this.resetPasswordForm.password) {
                this.resetPasswordErrors.password = "New password is required";
                isValid = false;
            } else if (this.resetPasswordForm.password.length < 8) {
                this.resetPasswordErrors.password = "Password must be at least 8 characters";
                isValid = false;
            }

            if (!this.resetPasswordForm.confirmPassword) {
                this.resetPasswordErrors.confirmPassword = "Please confirm the new password";
                isValid = false;
            } else if (this.resetPasswordForm.password !== this.resetPasswordForm.confirmPassword) {
                this.resetPasswordErrors.confirmPassword = "Passwords do not match";
                isValid = false;
            }

            return isValid;
        },

        getOriginalValue(key) {
            const user = this.user || {};
            const values = {
                firstname: user.firstname || user.firstName || "",
                lastname: user.lastname || user.lastName || "",
                email: user.email || "",
                phoneNumber: user.phoneNumber || user.phone || "",
                objectType: user.objectType || user.role || "",
                active: user.active !== undefined ? user.active : true,
                hospitalId: user.hospitalId || user.hospital?.id || user.hospital?._id || "",
                dob: this.formatDateInput(user.dob || user.dateOfBirth)
            };

            return values[key];
        },

        addIfChanged(payload, key, value) {
            if (value !== this.getOriginalValue(key)) {
                payload[key] = value;
            }
        },

        buildUpdatePayload() {
            const payload = {};
            const email = lowercaseString(this.form.email.trim());

            this.addIfChanged(payload, "firstname", this.form.firstName.trim());
            this.addIfChanged(payload, "lastname", this.form.lastName.trim());
            this.addIfChanged(payload, "email", email);
            this.addIfChanged(payload, "phoneNumber", this.form.phoneNumber?.trim() || "");
            this.addIfChanged(payload, "objectType", this.form.role);
            this.addIfChanged(payload, "active", this.form.active);
            this.addIfChanged(payload, "hospitalId", this.form.hospitalId);
            this.addIfChanged(payload, "dob", this.form.dob);

            return payload;
        },

        async handleSave() {
            if (!this.validate()) return;

            this.saving = true;
            try {
                const payload = this.editMode
                    ? this.buildUpdatePayload()
                    : {
                        firstname: this.form.firstName.trim(),
                        lastname: this.form.lastName.trim(),
                        email: lowercaseString(this.form.email.trim()),
                        phoneNumber: this.form.phoneNumber?.trim() || undefined,
                        objectType: this.form.role,
                        active: this.form.active,
                        hospitalId: this.form.hospitalId || undefined,
                        dob: this.form.dob || undefined
                    };

                if (!this.editMode) {
                    payload.password = this.form.password;
                }

                if (this.editMode && Object.keys(payload).length === 0) {
                    this.closeModal();
                    return;
                }

                const userId = this.user?._id || this.user?.id;
                const res = this.editMode
                    ? await UsersService.update(userId, payload)
                    : await UsersService.create(payload);

                if (res.data.status === 200 || res.data.status === 201) {
                    this.$emit("saved", res.data.data);
                    this.$emit("fetch-users");
                    this.closeModal();
                    this.$store.commit("toast/NEW", {
                        message: res?.data?.data?.message ||
                            (this.editMode ? "User updated successfully" : "User created successfully"),
                        type: "success"
                    });
                } else {
                    this.$store.commit("toast/NEW", {
                        message: res?.data?.message || "Something went wrong",
                        type: "error"
                    });
                }
            } catch (error) {
                console.error(error);
                const res = error?.response?.data;
                this.$store.commit("toast/NEW", {
                    message: res?.data?.message || res?.message || "Something went wrong",
                    type: "error"
                });
            } finally {
                this.saving = false;
            }
        },

        async handleResetPassword() {
            if (!this.validateResetPassword()) return;

            this.resettingPassword = true;
            try {
                const userId = this.user?._id || this.user?.id;
                const res = await UsersService.resetPassword(
                    userId,
                    this.resetPasswordForm.password
                );

                if (res.data.status === 200 || res.data.status === 201) {
                    this.resetResetPasswordForm();
                    this.$store.commit("toast/NEW", {
                        message: res?.data?.data?.message || "Password reset successfully",
                        type: "success"
                    });
                } else {
                    this.$store.commit("toast/NEW", {
                        message: res?.data?.message || "Failed to reset password",
                        type: "error"
                    });
                }
            } catch (error) {
                const res = error?.response?.data;
                this.$store.commit("toast/NEW", {
                    message: res?.data?.message || res?.message || "Failed to reset password",
                    type: "error"
                });
            } finally {
                this.resettingPassword = false;
            }
        },

        async handleDelete() {
            if (!confirm(`Are you sure you want to delete "${this.form.firstName} ${this.form.lastName}"? This action cannot be undone.`)) return;

            try {
                const userId = this.user?._id || this.user?.id;
                const res = await UsersService.delete(userId);

                if (res.data.status === 200) {
                    this.$emit("deleted", userId);
                    this.$emit("fetch-users");
                    this.closeModal();
                    this.$store.commit("toast/NEW", {
                        message: res?.data?.data?.message || "User deleted successfully",
                        type: "success"
                    });
                }
            } catch (error) {
                const res = error?.response?.data;
                this.$store.commit("toast/NEW", {
                    message: res?.data?.message || res?.message || "Something went wrong",
                    type: "error"
                });
            }
        },

        closeModal() {
            this.$emit("close");
        }
    }
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.2s ease-out;
}

.modal-container {
    background: white;
    border-radius: 16px;
    width: 90%;
    max-width: 760px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.3s ease-out;
}

.modal-header {
    padding: 20px 24px;
    border-bottom: 1px solid #eef2f6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8faff;
    border-radius: 16px 16px 0 0;
}

.modal-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #0144A6;
    display: flex;
    align-items: center;
    gap: 10px;
}

.modal-close {
    background: transparent;
    border: none;
    font-size: 1.25rem;
    color: #666;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    transition: all 0.2s;
}

.modal-close:hover {
    background: #eef2f6;
    color: #dc3545;
}

.modal-body {
    padding: 24px;
    overflow-y: auto;
    flex: 1;
}

.modal-footer {
    padding: 20px 24px;
    border-top: 1px solid #eef2f6;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    background: #f8faff;
    border-radius: 0 0 16px 16px;
}

/* Form Styles */
.form-label-theme {
    font-weight: 600;
    margin-bottom: 6px;
    font-size: 14px;
    color: #444;
    display: block;
}

.form-control-theme,
.form-select-theme {
    width: 100%;
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 15px;
    transition: all 0.2s;
    background: white;
}

.form-control-theme:focus,
.form-select-theme:focus {
    border-color: #3c78f9;
    outline: none;
    box-shadow: 0 0 0 3px rgba(60, 120, 249, 0.1);
}

.form-control-theme.is-invalid,
.form-select-theme.is-invalid {
    border-color: #dc3545;
}

.required {
    color: #dc3545;
    font-weight: bold;
}

.validation-error {
    color: #dc3545;
    font-size: 12px;
    margin-top: 4px;
    font-weight: 500;
    min-height: 18px;
}

/* Password field */
.password-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.password-wrapper .form-control-theme {
    padding-right: 42px;
}

.password-toggle {
    position: absolute;
    right: 12px;
    background: transparent;
    border: none;
    color: #888;
    cursor: pointer;
    font-size: 14px;
    padding: 0;
    transition: color 0.2s;
}

.password-toggle:hover {
    color: #3c78f9;
}

.reset-password-panel {
    background: #fff8e8;
    border: 1px solid #ffe1a3;
    border-radius: 12px;
    padding: 16px;
}

.reset-password-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.reset-password-header h5 {
    margin: 0 0 4px;
    color: #8a5a00;
    font-size: 16px;
    font-weight: 700;
}

.reset-password-header p {
    margin: 0;
    color: #6b7280;
    font-size: 13px;
}

/* Button Styles */
.btn-theme-secondary {
    background: #f1f3f5;
    color: #333;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
    cursor: pointer;
}

.btn-theme-secondary:hover {
    background: #e9ecef;
}

.btn-theme-success {
    background: #0144A6;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
    cursor: pointer;
}

.btn-theme-success:hover:not(:disabled) {
    background: #013179;
}

.btn-theme-success:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-theme-outline-danger {
    background: transparent;
    border: 2px solid #dc3545;
    color: #dc3545;
    border-radius: 8px;
    padding: 8px 20px;
    font-weight: 600;
    transition: all 0.2s;
    cursor: pointer;
}

.btn-theme-outline-danger:hover {
    background: #dc3545;
    color: white;
}

.btn-theme-warning {
    background: #ffba33;
    border: none;
    border-radius: 8px;
    color: #3d2a00;
    cursor: pointer;
    font-weight: 700;
    padding: 10px 20px;
    transition: all 0.2s;
}

.btn-theme-warning:hover:not(:disabled) {
    background: #f2a900;
}

.btn-theme-warning:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 768px) {
    .modal-container {
        width: 95%;
        max-height: 95vh;
    }

    .modal-body {
        padding: 16px;
    }

    .modal-footer {
        flex-direction: column;
    }

    .modal-footer button {
        width: 100%;
    }
}

/* Chrome, Safari, Edge, Opera */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type="number"] {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>
